/**
 * Maximum length of a Backstage entity `metadata.name`.
 */
const ENTITY_NAME_MAX_LENGTH = 63;

/**
 * Deterministically convert a raw repository name into a valid Backstage entity
 * name (`metadata.name`).
 *
 * Backstage requires names to match
 * `^[A-Za-z0-9]([A-Za-z0-9._-]*[A-Za-z0-9])?$` and be at most 63 characters —
 * i.e. no leading/trailing `.`, `-` or `_`. Raw GitHub repository names such as
 * `.github`, `.github-private` or `-mapanalytics-valhalla-vmalert-azurefunction`
 * violate this and get rejected during catalog processing.
 *
 * This helper is an IDENTITY function for names that are already valid (they do
 * not start/end with a separator and are within the length limit), so existing
 * entity names are left completely unchanged — only names that Backstage would
 * otherwise reject are transformed. Because it is deterministic, callers that
 * only hold the raw repository name (e.g. deep links built from Watchtower /
 * scorecard data) can use it to reconstruct the catalog entity name.
 *
 * @param name - the raw repository name.
 * @param fallbackSeed - used to build a deterministic name when `name` sanitizes
 *   to an empty string (e.g. a name made up entirely of separators). The GitHub
 *   database id is a good seed.
 */
export function sanitizeRepositoryEntityName(
  name: string,
  fallbackSeed?: string | number,
): string {
  const sanitized = name
    .replace(/[^A-Za-z0-9._-]/g, '-') // replace disallowed characters
    .replace(/^[-._]+/, '') // strip leading separators
    .replace(/[-._]+$/, '') // strip trailing separators
    .slice(0, ENTITY_NAME_MAX_LENGTH) // enforce max length
    .replace(/[-._]+$/, ''); // re-strip in case truncation left a separator

  if (sanitized.length > 0) {
    return sanitized;
  }

  // Pathological input (empty after sanitizing, e.g. "..." or "---"): fall back
  // to a deterministic, always-valid name.
  const seed = `${fallbackSeed ?? ''}`.replace(/[^A-Za-z0-9]/g, '');
  return `repo-${seed || 'unnamed'}`.slice(0, ENTITY_NAME_MAX_LENGTH);
}
